import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { AgentSkillKind } from "@common/types/agentSkill";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import { HStack } from "@design-system/HStack/HStack";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    AGENT_SKILL_CATEGORY_LABELS,
    AGENT_SKILL_LOAD_MODE_LABELS,
    CUSTOMIZED_LABEL,
    KICK_AUTHOR_LABEL,
    KICK_DEFAULT_LABEL,
    KICK_DEFAULT_OUTDATED_HINT,
    KICK_DEFAULT_OUTDATED_LABEL,
} from "../agentSkillCopy";
import { AgentSkillRow } from "../agentSkillsViewConfig";

type Cell<TValue> = CellContext<ViewTableFeatures, AgentSkillRow, TValue>;

export const AgentSkillTitleCell = memo(({ row }: Cell<string>) => (
    <ViewTableCell>
        <HStack as="span" align="center" gap={8}>
            <BreakableText showTooltip text={row.original.title} />
            {row.original.isDefault && (
                <Tag color="neutral" size="sm" outline>
                    {KICK_DEFAULT_LABEL}
                </Tag>
            )}
            {row.original.isCustomized && (
                <Tag color="neutral" size="sm" outline>
                    {CUSTOMIZED_LABEL}
                </Tag>
            )}
            {row.original.isDefaultOutdated && (
                <Tooltip content={KICK_DEFAULT_OUTDATED_HINT}>
                    <Tag color="accent" size="sm">
                        {KICK_DEFAULT_OUTDATED_LABEL}
                    </Tag>
                </Tooltip>
            )}
            {!row.original.isEnabled && (
                <Tag color="neutral" size="sm">
                    Disabled
                </Tag>
            )}
        </HStack>
    </ViewTableCell>
));

export const AgentSkillDescriptionCell = memo(({ row }: Cell<string>) => (
    <ViewTableCell>
        <span className="text-secondary text-truncate">
            {row.original.description}
        </span>
    </ViewTableCell>
));

export const AgentSkillCategoryCell = memo(
    ({ row }: Cell<AgentSkillRow["category"]>) => (
        <ViewTableCell>
            {AGENT_SKILL_CATEGORY_LABELS[row.original.category]}
        </ViewTableCell>
    ),
);

export const AgentSkillCreatedByCell = memo(
    ({ row }: Cell<AgentSkillRow["createdBy"]>) => {
        const actor = row.original.createdBy;
        if (row.original.kind === AgentSkillKind.KICK && actor === null) {
            return <ViewTableCell>{KICK_AUTHOR_LABEL}</ViewTableCell>;
        }
        return (
            <ViewTableCell>
                {actor ? (
                    <BreakableText
                        showTooltip
                        text={actor.name ?? actor.email}
                    />
                ) : (
                    <EmptyValue />
                )}
            </ViewTableCell>
        );
    },
);

export const AgentSkillLoadModeCell = memo(
    ({ row }: Cell<AgentSkillRow["loadMode"]>) => (
        <ViewTableCell>
            {AGENT_SKILL_LOAD_MODE_LABELS[row.original.loadMode]}
        </ViewTableCell>
    ),
);
